/**
 * Rewrites the H.264 profile-level-id of a server offer into a spelling this browser receives.
 *
 * The media server offers the camera's own profile. libwebrtc matches it exactly against the
 * browser's receive list: Chrome lists High (`6400xx`) and not Constrained High (`640Cxx`),
 * Safari the reverse, so no single value satisfies both. The server keeps the honest value and
 * the browser that needs the constrained spelling patches the offer before setRemoteDescription.
 * The camera stream itself is unchanged and both decode it; only the negotiation string differs.
 */

const kProfileLevelIdRe = /profile-level-id=([0-9a-fA-F]{6})/g;

/** constraint_set4 + constraint_set5 mark High as Constrained High. */
const kHighIdc = 0x64;
const kConstrainedHighFlags = 0x0c;
const kHex = 16;
/** profile_idc + constraint flags: the part of profile-level-id that decides a match. */
const kProfileHexChars = 4;

/**
 * Profiles this browser can receive, as the first four hex digits of profile-level-id in lower
 * case (profile_idc + constraint flags; the level is negotiated asymmetrically and never
 * decides). null when the browser cannot be asked.
 */
export function getReceivableH264Profiles(): Set<string> | null {
    if (typeof RTCRtpReceiver === 'undefined' || !RTCRtpReceiver.getCapabilities) { return null; }
    const caps = RTCRtpReceiver.getCapabilities('video');
    if (!caps) { return null; }
    const profiles = new Set<string>();
    for (const codec of caps.codecs) {
        if (codec.mimeType.toLowerCase() !== 'video/h264') { continue; }
        const profile = /profile-level-id=([0-9a-fA-F]{4})/.exec(codec.sdpFmtpLine ?? '')?.[1];
        if (profile) { profiles.add(profile.toLowerCase()); }
    }
    return profiles;
}

/** The constrained spelling of `profile` (4 lower-case hex digits), or null if it has none. */
function getConstrainedSpelling(profile: string): string | null {
    const idc = parseInt(profile.slice(0, 2), kHex);
    const flags = parseInt(profile.slice(2, kProfileHexChars), kHex);
    if (idc !== kHighIdc) { return null; }
    return profile.slice(0, 2) + (flags | kConstrainedHighFlags).toString(kHex).padStart(2, '0');
}

/**
 * Returns `sdp` with every H.264 profile-level-id this browser would reject replaced by its
 * constrained spelling, when the browser receives that one instead. Lines the browser accepts
 * as offered are left alone, so Chrome never sees a change.
 */
export function adaptH264ProfilesToBrowser(
    sdp: string, receivable: Set<string> | null = getReceivableH264Profiles(),
): string {
    if (!receivable) { return sdp; }
    return sdp.replace(kProfileLevelIdRe, (attribute: string, id: string) => {
        const profile = id.slice(0, kProfileHexChars).toLowerCase();
        if (receivable.has(profile)) { return attribute; }
        const alt = getConstrainedSpelling(profile);
        if (!alt || !receivable.has(alt)) { return attribute; }
        return `profile-level-id=${alt.toUpperCase()}${id.slice(kProfileHexChars)}`;
    });
}
